import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import type { PublicTrendingResponse, PublicTrendingVideo } from '../../lib/api/public'
import { getTrendingPublic } from '../../lib/api/public'
import { Play, Star, Users, Film, ArrowRight, Check, CassetteTape } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { LogoText } from '@/components/LogoText'
import { VerticalFilmPlaceholder } from '@/components/vertical-film-placeholder'

export function Welcome() {
  const [publicVideos, setPublicVideos] = useState<PublicTrendingVideo[]>([])
  const heroImages = [
    '/img/hero-bg.jpg',
    '/img/stock_photo_1.jpeg',
    '/img/stock_photo_2.jpeg',
    '/img/stock_photo_3.jpeg',
    '/img/stock_photo_4.jpeg',
    '/img/stock_photo_5.jpeg',
  ]
  const heroSlides = [
    {
      title: "Africa's Premier Vertical Drama Streaming Platform",
      subtitle: 'Experience captivating stories in vertical format. Immersive dramas, bold narratives, revolutionary entertainment.',
    },
    {
      title: 'Stories That Move You',
      subtitle: 'Binge-worthy vertical series made for your phone. Drama, romance, and thrillers from across the continent.',
    },
    {
      title: 'Made for Mobile. Made for You.',
      subtitle: 'Watch anywhere. Full-screen vertical episodes designed for how you really watch—on the go.',
    },
    {
      title: 'African Voices, Global Stage',
      subtitle: "Discover creators and stories from Africa. Original series and exclusive content you won't find anywhere else.",
    },
    {
      title: 'Vertical First. Drama That Sticks.',
      subtitle: "Short episodes, big impact. New drops regularly so there's always something fresh to watch.",
    },
    {
      title: 'Your Next Favorite Show Awaits',
      subtitle: "Join thousands of viewers. Start free, upgrade when you're ready. No commitment.",
    },
  ]
  const [currentHeroIndex, setCurrentHeroIndex] = useState(0)
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)')
    const update = () => setIsMobile(mq.matches)
    update()
    mq.addEventListener('change', update)
    return () => mq.removeEventListener('change', update)
  }, [])

  // On mobile, use fullscreen iframe pages so app auth fills the screen (no browser chrome).
  const appSignInUrl = '/app/signin'
  const appSignupUrl = '/app/signup'

  useEffect(() => {
    if (heroImages.length <= 1) return

    const interval = setInterval(() => {
      setCurrentHeroIndex((prev) => (prev + 1) % heroImages.length)
    }, 5000)

    return () => clearInterval(interval)
  }, [heroImages.length])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      const apiUrl = import.meta.env.VITE_API_URL as string | undefined
      const isProdApi = apiUrl?.includes('api.binyumatv.com')

      if (isLocalhost && isProdApi) {
        return
      }
    }

    getTrendingPublic(6)
      .then((res: PublicTrendingResponse) => {
        const valid = (res.videos || []).filter((v) => v?.id != null && String(v.id).trim() !== '')
        setPublicVideos(valid)
      })
      .catch(() => setPublicVideos([]))
  }, [])

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-4 sm:px-6 pt-4 sm:pt-6">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <img src="/img/logo.png" alt="Binyuma TV" className="w-9 h-9 sm:w-10 sm:h-10 object-contain" />
        </Link>
        <nav className="flex items-center gap-4 sm:gap-6">
          <Link to="/originals" className="text-sm text-gray-400 hover:text-[#D4AF37] transition-colors">Originals</Link>
          <Link to="/creators" className="text-sm text-gray-400 hover:text-[#D4AF37] transition-colors">Creators</Link>
          <Link to="/press" className="text-sm text-gray-400 hover:text-[#D4AF37] transition-colors">Press</Link>
          {isMobile ? (
            <Link to={appSignInUrl} className="text-sm font-medium text-[#D4AF37] hover:text-[#FFD700] transition-colors">Sign In</Link>
          ) : (
            <Link to="/login" className="text-sm font-medium text-[#D4AF37] hover:text-[#FFD700] transition-colors">Sign In</Link>
          )}
        </nav>
      </header>
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-black">
        <div className="absolute inset-0">
          {heroImages.map((img, index) => (
            <div
              key={img}
              className={`absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ${
                index === currentHeroIndex ? 'opacity-100' : 'opacity-0'
              }`}
              style={{
                backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.7), rgba(0,0,0,0.95)), url(${img})`,
              }}
            />
          ))}
        </div>
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_0,rgba(212,175,55,0.25),transparent_55%),radial-gradient(circle_at_80%_100%,rgba(74,31,184,0.4),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(212,175,55,0.1),transparent_55%)]" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 text-center">
          <div className="mb-12 relative">
            <img
              src="/img/logo.png"
              alt="Binyuma TV logo"
              className="w-50 h-50 md:w-30 md:h-30 mx-auto mb-6 object-contain drop-shadow-[0_0_25px_rgba(212,175,55,0.7)]"
            />
            <LogoText />
            <div className="min-h-[4.5rem] md:min-h-[5rem] mb-4 flex flex-col justify-center">
              <p
                key={currentHeroIndex}
                className="animate-hero-text-in text-2xl md:text-3xl text-gray-300"
              >
                {heroSlides[currentHeroIndex]?.title ?? heroSlides[0].title}
              </p>
            </div>
            <div className="min-h-[5rem] md:min-h-[6rem] mb-12 flex flex-col justify-center">
              <p
                key={`sub-${currentHeroIndex}`}
                className="animate-hero-text-in text-lg md:text-xl text-gray-400 max-w-2xl mx-auto [animation-delay:0.1s]"
              >
                {heroSlides[currentHeroIndex]?.subtitle ?? heroSlides[0].subtitle}
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16 w-full max-w-sm mx-auto sm:max-w-none">
            {isMobile ? (
              <Link to={appSignupUrl} className="w-full sm:flex-1 hover:cursor-pointer min-w-0">
                <Button size="lg" className="w-full bg-[#D72638] hover:bg-[#D72638]/90 text-white hover:text-black px-8 py-6 text-lg font-semibold rounded-xl shadow-[0_0_30px_rgba(215,38,56,0.5)]">
                  Create Account
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            ) : (
              <a href="/download" className="w-full sm:flex-1 hover:cursor-pointer min-w-0">
                <Button size="lg" className="w-full bg-[#D72638] hover:bg-[#D72638]/90 text-white hover:text-black px-8 py-6 text-lg font-semibold rounded-xl shadow-[0_0_30px_rgba(215,38,56,0.5)]">
                  Create Account
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
            )}
            {isMobile ? (
              <Link to={appSignInUrl} className="w-full sm:flex-1 min-w-0">
                <Button size="lg" variant="secondary" className="w-full px-8 py-6 text-lg font-semibold rounded-xl">
                  Sign In
                </Button>
              </Link>
            ) : (
              <Link to="/login" className="w-full sm:flex-1 min-w-0">
                <Button size="lg" variant="secondary" className="w-full px-8 py-6 text-lg font-semibold rounded-xl">
                  Sign In
                </Button>
              </Link>
            )}
          </div>
          {publicVideos.length > 0 && (
            <div className="mt-20">
              <h2 className="text-3xl font-bold mb-8">Featured Content</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
                {publicVideos.slice(0, 3).map((video) => (
                  <Link
                    key={video.id}
                    to={`/shows/${video.id}`}
                    className="group relative aspect-[1/2] rounded-xl overflow-hidden bg-gray-900 hover:scale-105 transition-transform"
                  >
                    {video.thumbnail_url ? (
                      <img src={video.thumbnail_url} alt={video.title} className="w-full h-full object-contain" />
                    ) : (
                      <VerticalFilmPlaceholder title={video.title} className="w-full h-full" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <h3 className="font-bold text-sm mb-1 line-clamp-2">{video.title}</h3>
                      </div>
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-16 h-16 rounded-full bg-[#D72638] flex items-center justify-center shadow-[0_0_30px_rgba(215,38,56,0.6)]">
                        <Play className="w-8 h-8 text-white fill-white ml-1" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
      <section className="py-20 px-6 bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16">
            Why Choose <span className="text-[#D4AF37]">Binyuma TV</span>?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="overflow-hidden rounded-xl bg-gradient-to-br from-[#4A1FB8]/10 to-transparent border border-[#bda4ff]/30 shadow-[0_18px_45px_rgba(0,0,0,0.65)]">
              <div className="relative h-64 w-full overflow-hidden">
                <img
                  src="/img/stock_photo_2.jpeg"
                  alt="Vertical drama on phone"
                  className="h-full w-full object-contain"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
              </div>
              <div className="text-center p-8">
              <div className="w-16 h-16 rounded-full bg-[#4A1FB8]/20 flex items-center justify-center mx-auto mb-6">
                <Film className="w-8 h-8 text-[#4A1FB8]" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Vertical-First Design</h3>
              <p className="text-gray-400">Optimized for mobile viewing with immersive 9:16 format. Perfect for on-the-go entertainment.</p>
              </div>
            </div>
            <div className="overflow-hidden rounded-xl bg-gradient-to-br from-[#D4AF37]/10 to-transparent border border-[#D4AF37]/30 shadow-[0_18px_45px_rgba(0,0,0,0.65)]">
              <div className="relative h-64 w-full overflow-hidden">
                <img
                  src="/img/stock_photo_3.jpeg"
                  alt="African city skyline at dusk"
                  className="h-full w-full object-contain"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
              </div>
              <div className="text-center p-8">
              <div className="w-16 h-16 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mx-auto mb-6">
                <CassetteTape className="w-8 h-8 text-[#D4AF37]" />
              </div>
              <h3 className="text-2xl font-bold mb-4">African Stories</h3>
              <p className="text-gray-400">Curated content showcasing the richness and diversity of African narratives and culture.</p>
              </div>
            </div>
            <div className="overflow-hidden rounded-xl bg-gradient-to-br from-[#f2741e]/10 to-transparent border border-[#f2741e]/30 shadow-[0_18px_45px_rgba(0,0,0,0.65)]">
              <div className="relative h-64 w-full overflow-hidden">
                <img
                  src="/img/stock_photo_4.jpeg"
                  alt="Creator behind the scenes"
                  className="h-full w-full object-contain"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
              </div>
              <div className="text-center p-8">
              <div className="w-16 h-16 rounded-full bg-[#f2741e]/20 flex items-center justify-center mx-auto mb-6">
                <Users className="w-8 h-8 text-[#f2741e]" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Creator Platform</h3>
              <p className="text-gray-400">Easy upload and management tools for creators to share their stories with the world.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="py-20 px-6 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16">
            What Our <span className="text-[#D4AF37]">Viewers</span> Say
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { name: 'Amina Okafor', role: 'Content Creator', text: 'Binyuma TV has revolutionized how I consume African content. The vertical format is perfect for mobile viewing!', rating: 5 },
              { name: 'Kwame Mensah', role: 'Film Enthusiast', text: 'Finally, a platform dedicated to African stories. The quality and diversity of content is incredible.', rating: 5 },
              { name: 'Zara Nwosu', role: 'Student', text: "I love how easy it is to discover new dramas. The recommendation system is spot on!", rating: 5 },
            ].map((t, idx) => (
              <div key={idx} className="p-6 rounded-xl bg-gradient-to-br from-gray-900 to-black border border-gray-800">
                <div className="flex items-center gap-1 mb-4">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-[#D4AF37] fill-[#D4AF37]" />
                  ))}
                </div>
                <p className="text-gray-300 mb-6 italic">&quot;{t.text}&quot;</p>
                <p className="font-bold">{t.name}</p>
                <p className="text-sm text-gray-500">{t.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-20 px-6 bg-gradient-to-b from-[#0a0a0a] to-black">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4">
            Choose Your <span className="text-[#D4AF37]">Plan</span>
          </h2>
          <p className="text-center text-gray-400 mb-16">Start watching today. No credit card required.</p>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { name: 'Free', price: '$0', period: 'forever', features: ['Access to public content', 'Basic video quality', 'Limited downloads', 'Community support', 'With ads'], cta: 'Get Started', popular: false },
              { name: 'Premium', price: '$4.99', period: 'per month', features: ['All content access', 'HD & 4K quality', 'Unlimited downloads', 'Ad-free experience', 'Early access to new releases', 'Priority support'], cta: 'Start Premium', popular: true },
              { name: 'Diamond', price: '$14.99', period: 'per month', features: ['Everything in Premium', 'Upload unlimited videos', 'Advanced analytics', 'Monetization tools', 'Creator dashboard', 'Dedicated support'], cta: 'Become Creator', popular: false },
            ].map((plan, idx) => (
              <div
                key={idx}
                className={`overflow-hidden rounded-xl border-2 ${plan.popular ? 'border-[#D4AF37] bg-gradient-to-br from-[#D4AF37]/10 to-transparent' : 'border-gray-800 bg-gradient-to-br from-gray-900 to-black'} shadow-[0_20px_55px_rgba(0,0,0,0.75)]`}
              >
                <div className="relative h-36 w-full overflow-hidden">
                  <img
                    src={
                      plan.name === 'Premium'
                        ? '/img/stock_photo_5.jpeg'
                        : plan.name === 'Diamond'
                          ? '/img/stock_photo_9.jpeg'
                          : '/img/stock_photo_7.jpeg'
                    }
                    alt={`${plan.name} plan art`}
                    className="h-full w-full object-contain"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                </div>
                <div className="p-8">
                {plan.popular && (
                  <div className="text-center mb-4">
                    <span className="inline-block px-4 py-1 rounded-full bg-[#D4AF37] text-black text-sm font-bold">Most Popular</span>
                  </div>
                )}
                <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                <div className="mb-6">
                  <span className="text-4xl font-bold">{plan.price}</span>
                  <span className="text-gray-400 ml-2">/{plan.period}</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300">{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/signup" className="block">
                  <Button className={`w-full ${plan.popular ? 'bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-black' : 'bg-[#D72638] hover:bg-[#D72638]/90 text-white'}`}>{plan.cta}</Button>
                </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-20 px-6 bg-gradient-to-b from-black via-[#4A1FB8]/10 to-black">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Start Watching?</h2>
          <p className="text-xl text-gray-400 mb-12">Join thousands of viewers discovering amazing African stories.</p>
          <Link to="/signup">
            <Button size="lg" className="bg-[#D72638] hover:bg-[#D72638]/90 text-white px-12 py-6 text-lg font-semibold rounded-xl shadow-[0_0_30px_rgba(215,38,56,0.5)]">
              Sign Up
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
      <footer className="py-12 px-6 border-t border-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-bold mb-4 text-[#D4AF37]">Binyuma TV</h3>
              <p className="text-gray-400 text-sm">Africa's premier vertical drama streaming platform.</p>
            </div>
            <div>
              <h4 className="font-bold mb-4">Platform</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/signup" className="hover:text-[#D4AF37]">Sign Up</Link></li>
                <li><Link to="/originals" className="hover:text-[#D4AF37]">Originals</Link></li>
                <li><Link to="/creators" className="hover:text-[#D4AF37]">Creators</Link></li>
                <li><Link to="/press" className="hover:text-[#D4AF37]">Press</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Support</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#" className="hover:text-[#D4AF37]">Help Center</a></li>
                <li><a href="#" className="hover:text-[#D4AF37]">Contact Us</a></li>
                <li><Link to="/terms" className="hover:text-[#D4AF37]">Terms of Service</Link></li>
                <li><Link to="/privacy" className="hover:text-[#D4AF37]">Privacy Policy</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Connect</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#" className="hover:text-[#D4AF37]">Twitter</a></li>
                <li><a href="#" className="hover:text-[#D4AF37]">Instagram</a></li>
                <li><a href="#" className="hover:text-[#D4AF37]">Facebook</a></li>
              </ul>
            </div>
          </div>
          <div className="text-center text-sm text-gray-500 pt-8 border-t border-gray-900">
            <p>&copy; {new Date().getFullYear()} Binyuma TV. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}
